import { Component } from '@angular/core';
import { formatChartJSTooltip } from '@emma-helpers/emma-utils';
import { formatDateLabel, formatNumber } from '@emma-helpers/format.helper';
import { ChartOptions, SerialData } from 'emma-common-ts/emma';
import { sortBy } from 'lodash';

import Chart from 'chart.js';
import { EMMAChartBase } from './emma-chart-base';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const mUtil: any;

const styles = ['altgreen', 'altdarkblue', 'altblue', 'altviolet', 'altyellow', 'altsoftorange'];

interface Data extends SerialData {
  variation?: number;
  total?: number;
}
type Options = Pick<ChartOptions, 'limit' | 'showLegend' | 'keepOrder' | 'customFooterLabels' | 'keepZeros'>;

const defaultOptions: Options = {
  limit: 10,
  showLegend: true,
  keepOrder: false,
  customFooterLabels: false,
  keepZeros: false,
};

@Component({
    selector: 'emma-donutchart',
    templateUrl: './emma-chart.template.html',
    standalone: false
})
export class EMMADonutChartComponent extends EMMAChartBase {
  data: Data = { labels: [], values: {} };
  override footerLabels: string[] = [];
  protected override options: Options = defaultOptions;
  override init() {
    const el = document.getElementById(this.chartId) as HTMLCanvasElement;
    if (!el) {
      throw new Error('Chart not found: ' + this.chartId);
    }
    const ctx = el.getContext('2d');
    if (!ctx) {
      throw new Error('Chart without Context2D: ' + this.chartId);
    }
    const config: Chart.ChartConfiguration = {
      type: 'doughnut',
      data: {
        labels: [],
        datasets: [],
      },
      options: this.getOptions(),
    };

    this.chart = new Chart(ctx, config);
    this.chartLoad.emit(true);
    if (this.export) {
      this.export.subscribe(() => this.exportChart());
    }
  }
  override getStyle = (i = 0) => styles[i % styles.length];

  protected override _updateChart() {
    if (!this.chart) {
      console.error('No chart found');
      return;
    }
    const dataset = {
      label: [this.data.label],
      backgroundColor: this.data.labels.map((__, i) => mUtil.getColor(this.getStyle(i))),
      data: this.data.labels.map((label) => this.data.values[label] || 0),
    };
    this.chart.data.labels = this.data.labels;
    this.chart.data.datasets = [dataset];
    this.chart.options = this.getOptions();
    this.chart.update();
  }

  override update(data: Data | null, options: Partial<Options> = {}) {
    if (data && data.error) {
      return this.error(data.error);
    } else if (!data || data.loading || !data.values) {
      return this.loading();
    }
    this.options = { ...defaultOptions, ...options };
    this.loaded();

    // Clean empty labels, sort by descending value if needed
    const labels = this.options.keepOrder
      ? data.labels
          .filter((label) => Boolean(data.values[label] || this.options.keepZeros))
          .slice(0, this.options.limit)
      : sortBy(
          data.labels
            .filter((label) => Boolean(data.values[label]) || this.options.keepZeros)
            .slice(0, this.options.limit),
          (label) => -data.values[label]
        );
    const total = labels.reduce((sum, key) => sum + data.values[key] || 0, 0);
    if (!this.options.customFooterLabels) {
      this.footerLabels = labels.map((label) => {
        const value = data.values[label];
        const percent = formatNumber(total ? value / total : 0, {
          style: 'percent',
          maximumFractionDigits: 2,
        });
        const units = value === 1 ? this.unit || this.units : this.units;
        return `${formatNumber(value)} ${units} (${percent}) ${label}`;
      });
    }

    this.data = { values: { ...data.values }, labels, label: data.label };
    this.updateVariation(data.variation);
    this.updateTotal(data.total || total);
    this._updateChart();
  }

  protected getOptions(): Chart.ChartOptions {
    return {
      title: {
        display: false,
      },
      tooltips: {
        intersect: false,
        mode: 'nearest',
        xPadding: 10,
        yPadding: 10,
        caretPadding: 10,
        callbacks: {
          label: formatChartJSTooltip,
        },
      },
      legend: {
        display: Boolean(this.options.showLegend),
        position: 'bottom',
        labels: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          generateLabels: (chart: any) =>
            chart.data.labels.map((label: string, i: number) => {
              return {
                text: formatDateLabel(label),
                fillStyle: !chart.data.datasets[0].data[i]
                  ? '#dddddd'
                  : chart.config.data.datasets[0].backgroundColor[i],
                index: i,
              };
            }),
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
      cutoutPercentage: 60,
      animation: this.export ? { duration: 0 } : { duration: 500, easing: 'easeOutQuart' },
    };
  }
}
